/**
 * UTM / referrer sanitization for analytics attribution.
 * Shared limits align with api/lib/sanitizeUtmParams.ts on the server.
 */

import type { UTMParameters } from "./utmCapture";

export const UTM_PARAM_MAX_LENGTH = 256;
export const REFERRER_MAX_LENGTH = 2048;
export const LANDING_PAGE_MAX_LENGTH = 512;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const satisfies ReadonlyArray<keyof UTMParameters>;

/** Normalize hostname for same-site checks (lowercase, strip www.). */
export function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, "");
}

/**
 * True when referrer is a parseable http(s) URL whose host differs from the site.
 * Uses origin hostname comparison — not substring matching on the full URL.
 */
export function isExternalReferrer(
  referrer: string,
  siteHostname: string,
): boolean {
  try {
    const refUrl = new URL(referrer);
    if (refUrl.protocol !== "http:" && refUrl.protocol !== "https:") {
      return false;
    }
    return (
      normalizeHostname(refUrl.hostname) !== normalizeHostname(siteHostname)
    );
  } catch {
    return false;
  }
}

function stripControlChars(value: string): string {
  return value.replace(/[\u0000-\u001F\u007F]/g, "");
}

export function sanitizeUtmField(
  value: string,
  maxLength: number,
): string | undefined {
  const cleaned = stripControlChars(value).trim();
  if (!cleaned) {
    return undefined;
  }
  return cleaned.slice(0, maxLength);
}

export function sanitizeReferrer(value: string): string | undefined {
  const cleaned = sanitizeUtmField(value, REFERRER_MAX_LENGTH);
  if (!cleaned) {
    return undefined;
  }
  try {
    const url = new URL(cleaned);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return undefined;
    }
    return cleaned;
  } catch {
    return undefined;
  }
}

/** Drop invalid/oversized UTM fields; safe to run on URL input and stored JSON. */
export function sanitizeUtmParameters(params: UTMParameters): UTMParameters {
  const out: UTMParameters = {};

  for (const key of UTM_KEYS) {
    const raw = params[key];
    if (typeof raw === "string") {
      const sanitized = sanitizeUtmField(raw, UTM_PARAM_MAX_LENGTH);
      if (sanitized) {
        out[key] = sanitized;
      }
    }
  }

  if (typeof params.referrer === "string") {
    const referrer = sanitizeReferrer(params.referrer);
    if (referrer) {
      out.referrer = referrer;
    }
  }

  if (typeof params.landing_page === "string") {
    const landingPage = sanitizeUtmField(
      params.landing_page,
      LANDING_PAGE_MAX_LENGTH,
    );
    if (landingPage) {
      out.landing_page = landingPage;
    }
  }

  return out;
}
